import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import AsyncSelect from 'react-select/async';
import { connect } from 'react-redux';
import Select from 'react-select';
import memoize from 'memoize-one';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Mutex } from 'async-mutex';
import { nsOptions } from '../i18n';
import { childrenPropTypes } from '../utils/generic-prop-types';
import NewModal from './NewModal';
import ButtonConfirm from './ButtonConfirm';
import LabeledInput from './LabeledInput';
import LabeledChoice from './LabeledChoice';
import FAQLink from './FAQLink';
import Help from './Help';
import {
  ELEMENT_TYPE_MEASUREMENT, ELEMENT_TYPE_CALCULATION, ELEMENT_TYPE_DATE, DATE_TIME, DATE_FULL,
  DATE_MONTH_YEAR, DATE_YEAR, DATE_FORMATS, ELEMENT_TYPE_TEXT, ELEMENT_TYPE_MULTIPLE_CHOICES,
  ELEMENT_TYPE_UNIQUE_CHOICE,
} from '../constants';
import {
  checkSettingsValidity, checkDateValidity, checkTextSettingsValidity,
} from '../utils/consistency-check';
import ErrorUtil from '../utils/ErrorUtil';
import Toast from '../utils/Toast';
import ElementUtil from '../utils/ElementUtil';
import SortUtil from '../utils/SortUtil';
import { API_DATE_TIME_FORMAT } from '../utils/date';
import Checkbox from './Checkbox';
import DatePicker from './DatePicker';
import NewTooltip from './NewTooltip';
import { getDateInputPlaceholder } from './ElementDate';
import { CardLoader } from './Loader';

const SPECIFIC_VALUE = 'value';
const SPECIFIC_DATE = 'date';
const INTERVAL = 'interval';
const NO_TYPE_CHOSEN = 'none';
const CUSTOM_DATE = 'custom-date';
const FORM_DATE = 'form-date';
const INCLUSION_CREATION_DATE = 'inclusion-creation-date';
const FORMAT_UPPERCASE = 'A';
const FORMAT_LOWERCASE = 'a';
const FORMAT_DIGIT = '1';
const FORMAT_DASH = '-';
const FORMAT_UNDERSCORE = '_';
const FORMAT_SLASH = '/';

const mapStateToProps = (state) => ({
  elements: state.elements,
  projectElements: state.projectElements,
  pages: state.projectPages,
  user: state.auth.authUser,
});

const getFormatOptions = (t) => [
  { value: FORMAT_UPPERCASE, label: t('project:consistency-check.format-uppercase'), shortLabel: 'A' },
  { value: FORMAT_LOWERCASE, label: t('project:consistency-check.format-lowercase'), shortLabel: 'a' },
  { value: FORMAT_DIGIT, label: t('project:consistency-check.format-digit'), shortLabel: '1' },
  { value: FORMAT_DASH, label: t('project:consistency-check.format-dash'), shortLabel: '-' },
  { value: FORMAT_UNDERSCORE, label: t('project:consistency-check.format-underscore'), shortLabel: '_' },
  { value: FORMAT_SLASH, label: t('project:consistency-check.format-slash'), shortLabel: '/' },
];

const formatOptionLabel = (option, { context }) => {
  if (context === 'value') {
    // Display only short label when selected
    return option.shortLabel || option.value;
  }
  // Display full label in dropdown menu
  return option.label;
};

const TEXT_CONSTRAINT_NONE = 'none';
const TEXT_CONSTRAINT_MIN_MAX = 'min_max';
const TEXT_CONSTRAINT_FIXED = 'fixed';

const MAX_FIXED_LENGTH = 15;

const formatPatternSelectStyles = {
  container: (provided) => ({
    ...provided,
    width: '50px',
    marginRight: '20px',
    marginBottom: '10px',
  }),
  control: (provided) => ({
    ...provided,
    minHeight: '32px',
    height: '32px',
    fontSize: '13px',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid var(--gray-light)',
    borderRadius: '0',
    boxShadow: 'none',
    '&:hover': {
      borderBottom: '2px solid var(--gray-light)',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '32px',
    padding: '0 8px',
    justifyContent: 'center',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '32px',
    padding: '0',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '4px',
    color: 'var(--gray)',
    svg: {
      width: '14px',
      height: '14px',
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '4px',
    svg: {
      width: '14px',
      height: '14px',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '18px',
    fontWeight: '700',
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: '13px',
    minWidth: '180px',
    zIndex: 9999,
  }),
  option: (provided) => ({
    ...provided,
    padding: '6px 10px',
    fontWeight: '600',
    fontSize: '14px',
  }),
};

@withTranslation('', nsOptions)
class LimitValueField extends Component {
  static propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
    defaultValue: PropTypes.string,
    value: PropTypes.string,
    className: PropTypes.string,
    onKeyPress: PropTypes.func,
    labelClassName: PropTypes.string,
    min: PropTypes.string,
  };

  static defaultProps = {
    label: '',
    placeholder: '',
    defaultValue: null,
    value: undefined,
    className: '',
    labelClassName: '',
    onKeyPress: () => {},
    min: undefined,
  };

  render() {
    const {
      label, name, placeholder, type, onChange, defaultValue, value, className, onKeyPress,
      labelClassName, min,
    } = this.props;

    // Use value (controlled) if provided, otherwise use defaultValue (uncontrolled)
    const inputProps = value !== undefined
      ? { value }
      : { defaultValue };

    return (
      <div className={className}>
        <LabeledInput
          label={label}
          name={name}
          type={type}
          placeholder={placeholder}
          onChange={onChange}
          {...inputProps}
          className="mb-0"
          onKeyPress={onKeyPress}
          inputClassName="labeled-input limit-value"
          labelClassName={labelClassName}
          hideOptionalLabel
          min={min}
        />
      </div>
    );
  }
}

@withTranslation('', nsOptions)
class DateInput extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    elements: PropTypes.shape().isRequired,
    projectElement: PropTypes.shape(),
    projectElements: PropTypes.shape().isRequired,
    pages: PropTypes.shape().isRequired,
    onChange: PropTypes.func.isRequired,
    customDate: PropTypes.string,
    inclusionDate: PropTypes.bool,
    onDateTypeChange: PropTypes.func.isRequired,
    formDate: PropTypes.number,
    format: PropTypes.oneOf([
      DATE_TIME,
      DATE_FULL,
      DATE_MONTH_YEAR,
      DATE_YEAR,
    ]),
    castToDate: PropTypes.bool,
    onCastChange: PropTypes.func.isRequired,
    checkboxId: PropTypes.string,
  };

  static defaultProps = {
    projectElement: null,
    customDate: null,
    inclusionDate: false,
    formDate: null,
    castToDate: false,
    format: DATE_FULL,
    checkboxId: 'cast-value-to-date',
  };

  constructor(props) {
    super(props);
    this.state = {
      dateType: DateInput.getInitialState(props),
    };
  }

  static getInitialState = (props) => {
    if (props.inclusionDate) {
      return INCLUSION_CREATION_DATE;
    }
    if (props.formDate) {
      return FORM_DATE;
    }
    return CUSTOM_DATE;
  }

  getVariables = async (search, elements, projectElements, projectElement, pages) => {
    const { t } = this.props;

    // Filter project elements
    const filteredProjectElements = Object.values(projectElements)
      .filter((pEl) => pEl.id !== projectElement.id
        && (elements[pEl.element].type === ELEMENT_TYPE_DATE)
        && ((search
          && ElementUtil.formatElementName(elements[pEl.element], t).toLowerCase().includes(
            search.toLowerCase(),
          ))
            || !search)
            && (pEl.module === null || pEl.module === projectElement.module));

    // Order project elements by page and sorting
    const pElementsByPage = {};
    filteredProjectElements.forEach((pEl) => {
      const id = pages[pEl.project_page].sorting;
      if (pElementsByPage[id]) pElementsByPage[id].push(pEl);
      else pElementsByPage[id] = [pEl];
    });

    const orderedProjectElements = [];
    Object.values(pElementsByPage).forEach((pElements) => {
      orderedProjectElements.push(...pElements.sort((a, b) => {
        if (a.module !== b.module) return b.module ? -1 : 1;
        return SortUtil.sortArray(a, b);
      }));
    });

    // Format results
    return orderedProjectElements.map((pEl) => this.formatProjectElement(pEl));
  }

  formatProjectElement = (pEl) => {
    const {
      t, elements, pages, projectElements,
    } = this.props;
    const label = ElementUtil.formatProjectElementName(pEl, projectElements, elements, pages, t);
    return {
      value: pEl.id,
      label,
    };
  }

  onChange = (e) => {
    const { projectElement, projectElements, elements } = this.props;
    const { dateType } = this.state;
    if (dateType === CUSTOM_DATE) {
      this.props.onChange(CUSTOM_DATE, e);
    } else if (dateType === FORM_DATE) {
      const pId = e ? e.value : null;
      let truncTo = null;
      if (pId) {
        // The element that is being configured
        const parentElement = elements[projectElement.element];
        // The element that has been selected in the AsyncSelect
        const childrenPrjElement = Object.values(projectElements).find((pEl) => pEl.id === pId);
        const childrenElement = elements[childrenPrjElement.element];
        if (parentElement.format !== childrenElement.format) {
          // Saves the API from having to calculate wether to cast to date or not
          truncTo = DATE_FORMATS.find((format) => (
            [parentElement.format, childrenElement.format].includes(format)
          ));
        }
      }
      this.props.onChange(FORM_DATE, pId, truncTo);
    }
  }

  onDateTypeChange = (option) => {
    let newDateType;
    if (option) {
      newDateType = option.value;
    } else {
      // When we delete the INCLUSION_CREATION_DATE option
      newDateType = CUSTOM_DATE;
    }
    this.props.onDateTypeChange(newDateType);
    this.setState({ dateType: newDateType });
  }

  render() {
    const {
      t, elements, projectElements, projectElement, pages, customDate, inclusionDate,
      formDate, castToDate, format, onCastChange, checkboxId,
    } = this.props;
    const { dateType } = this.state;

    const selectOptions = [
      { value: CUSTOM_DATE, label: t('project:consistency-check.custom-date') },
      { value: FORM_DATE, label: t('project:consistency-check.form-date') },
      { value: INCLUSION_CREATION_DATE, label: t('project:consistency-check.inclusion-date') },
    ];
    const [customOption, formOption, IncOption] = selectOptions;
    let selectOption;
    if (dateType === CUSTOM_DATE) {
      selectOption = customOption;
    } else if (dateType === FORM_DATE) {
      selectOption = formOption;
    } else {
      selectOption = IncOption;
    }

    let defaultCustomDate = null;
    if (customDate) {
      defaultCustomDate = moment(customDate);
    }

    let defaultFormDate = null;
    if (formDate) {
      const prjElement = Object.values(projectElements).find((pEl) => pEl.id === formDate);
      defaultFormDate = this.formatProjectElement(prjElement);
    }

    let formElement;
    if (formDate) {
      const pElement = Object.values(projectElements).find((pEl) => pEl.id === formDate);
      formElement = elements[pElement.element];
    }

    const showWarning = (formElement && formElement.format !== format)
          || (inclusionDate && inclusionDate.format !== format);

    return (
      <>
        <div className="row align-items-end">
          <div className="col-auto">
            <Select
              name="date-type-of-control"
              id=""
              className="react-select date-type-select"
              classNamePrefix="react-select"
              value={selectOption}
              isClearable={dateType === INCLUSION_CREATION_DATE}
              onChange={this.onDateTypeChange}
              styles={{
                singleValue: (provided) => ({
                  ...provided,
                  maxWidth: 'unset',
                  minWidth: '120px',
                }),
              }}
              options={selectOptions}
              isSearchable={false}
            />
          </div>
          {
            (dateType === CUSTOM_DATE) && (
              <div className="consistency-check">
                <div className="col-auto">
                  <DatePicker
                    placeholder={getDateInputPlaceholder(t, format)}
                    defaultValue={defaultCustomDate}
                    onChange={this.onChange}
                    format={format}
                  />
                </div>
              </div>
            )
          }
          {
            (dateType === FORM_DATE) && (
              <div className="col-5">
                <AsyncSelect
                  className="react-select date-variable-select"
                  classNamePrefix="react-select"
                  placeholder={t('project:consistency-check.choose-variable')}
                  noOptionsMessage={() => t('project:consistency-check.no-more-variables')}
                  loadOptions={(search) => this.getVariables(
                    search, elements, projectElements, projectElement, pages,
                  )}
                  onChange={this.onChange}
                  menuPlacement="auto"
                  hideSelectedOption
                  closeMenuOnSelect
                  defaultOptions
                  defaultValue={defaultFormDate}
                  size="25"
                  isClearable
                />
              </div>
            )
          }
        </div>
        {
          showWarning && (
            <div className="pt-2 text-red">
              <FontAwesomeIcon
                icon={['fas', 'exclamation-triangle']}
                transform="grow-2"
                style={{
                  '--fa-primary-opacity': 1,
                  '--fa-secondary-opacity': 0.4,
                }}
              />
              <span className="ml-2">
                {t('error:warning.comparison-on-date-with-mismatching-types')}
              </span>
            </div>
          )
        }
        {
          format === DATE_TIME && dateType === FORM_DATE && formElement
            && formElement.format === DATE_TIME && (
            <div className="pt-2">
              <Checkbox
                id={checkboxId}
                checked={castToDate}
                onChange={(e) => onCastChange(e.target.checked)}
              >
                {t('project:consistency-check.dont-take-time-into-account')}
              </Checkbox>
            </div>
          )
        }
      </>
    );
  }
}

@withToastManager
@connect(mapStateToProps)
@withTranslation('', nsOptions)
class ConsistencyCheckManager extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    children: childrenPropTypes().isRequired,
    elementName: PropTypes.string.isRequired,
    element: PropTypes.shape().isRequired,
    methods: PropTypes.shape().isRequired,
    elements: PropTypes.shape().isRequired,
    hideValidationToast: PropTypes.bool,
    projectElement: PropTypes.shape(),
    projectElements: PropTypes.shape().isRequired,
    pages: PropTypes.shape().isRequired,
    user: PropTypes.shape().isRequired,
  };

  static defaultProps = {
    hideValidationToast: false,
    projectElement: null,
  };

  static getInitialState = (element) => {
    const loading = false;
    if (element.type === ELEMENT_TYPE_DATE) {
      const { before, after } = element;
      const beforeVar = element.before_var;
      const afterVar = element.after_var;
      const lessThanIncDate = element.less_than_inc_date;
      const greaterThanIncDate = element.greater_than_inc_date;
      const truncMin = element.trunc_min;
      const truncMax = element.trunc_max;
      let typeOfSettings = INTERVAL;
      if ((before && after && before === after)
        || (greaterThanIncDate && lessThanIncDate)
        || (beforeVar && afterVar && beforeVar === afterVar)) {
        typeOfSettings = SPECIFIC_DATE;
      } else if (!(before || after || greaterThanIncDate || lessThanIncDate || beforeVar
        || afterVar)) {
        typeOfSettings = null;
      }
      return {
        loading,
        before,
        after,
        beforeVar,
        afterVar,
        lessThanIncDate,
        greaterThanIncDate,
        typeOfSettings,
        truncMin,
        truncMax,
        isConstraining: element.is_constraining || false,
      };
    }
    if (element.type === ELEMENT_TYPE_TEXT) {
      // eslint-disable-next-line max-len
      const hasFixedLength = element.text_fixed_length !== undefined && element.text_fixed_length !== null;
      // eslint-disable-next-line max-len
      const hasMinMaxLength = (element.text_min_length !== undefined && element.text_min_length !== null)
        || (element.text_max_length !== undefined && element.text_max_length !== null);

      // Determine constraint type: none by default, or the configured one
      let constraintType = TEXT_CONSTRAINT_NONE;
      if (hasFixedLength) {
        constraintType = TEXT_CONSTRAINT_FIXED;
      } else if (hasMinMaxLength) {
        constraintType = TEXT_CONSTRAINT_MIN_MAX;
      }

      // Get allow letters/digits booleans (default to true)
      const textAllowLetters = element.text_allow_letters !== undefined
        ? element.text_allow_letters : true;
      const textAllowDigits = element.text_allow_digits !== undefined
        ? element.text_allow_digits : true;

      return {
        loading,
        textConstraintType: constraintType,
        // Min/Max
        textMinLength: element.text_min_length !== undefined ? element.text_min_length.toString() : '',
        textMaxLength: element.text_max_length !== undefined ? element.text_max_length.toString() : '',
        textExcludeLimits: element.text_exclude_limits || false,
        textAllowLetters,
        textAllowDigits,
        // Longueur fixe
        textFixedLength: element.text_fixed_length !== undefined ? element.text_fixed_length.toString() : '',
        // eslint-disable-next-line max-len
        textFormatEnabled: element.text_format_pattern !== undefined && element.text_format_pattern !== null,
        textFormatPattern: ConsistencyCheckManager.parseRegexToPattern(element.text_format_pattern || ''),
        isConstraining: element.is_constraining || false,
      };
    }

    // eslint-disable-next-line max-len
    if ((element.type === ELEMENT_TYPE_UNIQUE_CHOICE || element.type === ELEMENT_TYPE_MULTIPLE_CHOICES)
          && element.implementable) {
      const hasFixedLength = element.list_item_fixed_length !== undefined
              && element.list_item_fixed_length !== null;
      // eslint-disable-next-line max-len
      const hasMinMaxLength = (element.list_item_min_length !== undefined && element.list_item_min_length !== null)
        || (element.list_item_max_length !== undefined && element.list_item_max_length !== null);

      // Determine constraint type: none by default, or the configured one
      let constraintType = TEXT_CONSTRAINT_NONE;
      if (hasFixedLength) {
        constraintType = TEXT_CONSTRAINT_FIXED;
      } else if (hasMinMaxLength) {
        constraintType = TEXT_CONSTRAINT_MIN_MAX;
      }

      // Get allow letters/digits booleans (default to true)
      const listItemAllowLetters = element.list_item_allow_letters !== undefined
        ? element.list_item_allow_letters : true;
      const listItemAllowDigits = element.list_item_allow_digits !== undefined
        ? element.list_item_allow_digits : true;

      return {
        loading,
        // Type de contrainte
        listConstraintType: constraintType,

        // Mode Min/Max
        listItemMinLength: element.list_item_min_length !== undefined ? element.list_item_min_length.toString() : '',
        listItemMaxLength: element.list_item_max_length !== undefined ? element.list_item_max_length.toString() : '',
        listItemExcludeLimits: element.list_item_exclude_limits || false,
        listItemAllowLetters,
        listItemAllowDigits,

        // Mode Longueur fixe
        listItemFixedLength: element.list_item_fixed_length !== undefined ? element.list_item_fixed_length.toString() : '',
        // eslint-disable-next-line max-len
        listItemFormatEnabled: element.list_item_format_pattern !== undefined && element.list_item_format_pattern !== null,
        listItemFormatPattern: ConsistencyCheckManager.parseRegexToPattern(element.list_item_format_pattern || ''),
        isConstraining: element.is_constraining || false,
      };
    }

    const lowBound = (element.min !== undefined) ? element.min.toString() : '';
    const highBound = (element.max !== undefined) ? element.max.toString() : '';
    const excludeLimits = element.strict || false;
    const forbidDecimals = element.integer || null;
    let typeOfSettings = NO_TYPE_CHOSEN;
    if (element.type === ELEMENT_TYPE_CALCULATION) {
      if ((lowBound !== '') && (highBound !== '') && (lowBound === highBound)) {
        typeOfSettings = SPECIFIC_VALUE;
      }
      if (lowBound !== highBound) {
        typeOfSettings = INTERVAL;
      }
    }
    return {
      loading,
      lowBound,
      highBound,
      excludeLimits,
      forbidDecimals,
      typeOfSettings,
      isConstraining: element.is_constraining || false,
    };
  };

  static parseRegexToPattern = (regexString) => {
    if (!regexString || regexString === '') {
      return [];
    }

    let cleanRegex = regexString;
    if (cleanRegex.startsWith('^')) {
      cleanRegex = cleanRegex.substring(1);
    }
    if (cleanRegex.endsWith('$')) {
      cleanRegex = cleanRegex.substring(0, cleanRegex.length - 1);
    }

    const patternMap = {
      '[A-Z]': FORMAT_UPPERCASE,
      '[a-z]': FORMAT_LOWERCASE,
      '[0-9]': FORMAT_DIGIT,
      '-': FORMAT_DASH,
      _: FORMAT_UNDERSCORE,
      '\\/': FORMAT_SLASH,
      '/': FORMAT_SLASH,
    };

    const pattern = [];
    let i = 0;

    while (i < cleanRegex.length) {
      let matched = false;

      // eslint-disable-next-line no-loop-func
      Object.entries(patternMap).some(([regex, char]) => {
        if (cleanRegex.substring(i).startsWith(regex)) {
          pattern.push(char);
          i += regex.length;
          matched = true;
          return true;
        }
        return false;
      });

      if (!matched) {
        i += 1;
      }
    }

    // Return array of values only (not objects with labels)
    // Labels will be added during render using getFormatOptions(t)
    return pattern;
  };

  constructor(props) {
    super(props);
    this.state = ConsistencyCheckManager.getInitialState(props.element);
    this.memoizedCheckSettingsValidity = memoize(checkSettingsValidity);
    this.mutex = new Mutex();
  }

  componentDidUpdate(prevProps) {
    if (this.props.element.strict !== prevProps.element.strict) {
      this.resetExcludeLimits(this.props.element.strict);
    }
    if (this.props.element.integer !== prevProps.element.integer) {
      this.resetForbidDecimals(this.props.element.integer);
    }
  }

  getCalculationTypeOfSettings = () => {
    const { min, max } = this.props.element;
    // For a specific value, min and max are equal in BDD
    if ((min !== undefined) && (max !== undefined) && (min === max)) {
      return SPECIFIC_VALUE;
    }
    if (min !== max) {
      return INTERVAL;
    }
    return NO_TYPE_CHOSEN;
  }

  resetSettingsParameters = () => {
    this.setState({
      lowBound: '',
      highBound: '',
      excludeLimits: false,
      forbidDecimals: false,
    });
  }

  resetExcludeLimits = (strict) => {
    this.setState({ excludeLimits: strict || false });
  }

  resetForbidDecimals = (integer) => {
    this.setState({ forbidDecimals: integer || null });
  }

  resyncSettingsParameters = () => {
    const { element } = this.props;
    this.setState(ConsistencyCheckManager.getInitialState(element));
  }

  deleteSettings = async () => {
    const { methods, hideValidationToast, element } = this.props;
    if (methods.patchElement && !this.mutex.isLocked()) {
      const release = await this.mutex.acquire();
      this.setState({ loading: true });
      let settings = {};
      if (element.type === ELEMENT_TYPE_DATE) {
        settings = {
          before: null,
          after: null,
          before_var: null,
          after_var: null,
          less_than_inc_date: null,
          greater_than_inc_date: null,
          truncMin: null,
          truncMax: null,
          is_constraining: null,
        };
      } else {
        settings = {
          min: null,
          max: null,
          strict: null,
          integer: null,
          is_constraining: null,
        };
      }
      try {
        await methods.patchElement({ ...settings });
        this.resetSettingsParameters();
        this.modal.hide();
        if (!hideValidationToast) {
          Toast.success(this.props, 'error:valid.saved');
        }
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      } finally {
        release();
        this.setState({ loading: false });
      }
    }
  }

    validateTextPattern = (pattern) => {
      if (!pattern || pattern.length === 0) return true;
      return pattern.every((item) => item !== null && item !== undefined && item !== '');
    };

    deleteListSettings = async () => {
      const { methods, hideValidationToast } = this.props;
      if (methods.patchElement && !this.mutex.isLocked()) {
        const release = await this.mutex.acquire();
        this.setState({ loading: true });
        try {
          await methods.patchElement({
            list_item_min_length: null,
            list_item_max_length: null,
            list_item_exclude_limits: null,
            list_item_allow_letters: null,
            list_item_allow_digits: null,
            list_item_fixed_length: null,
            list_item_format_pattern: null,
            is_constraining: null,
          });
          this.setState({
            listItemMinLength: '',
            listItemMaxLength: '',
            listItemExcludeLimits: false,
            listItemAllowLetters: true,
            listItemAllowDigits: true,
            listItemFixedLength: '',
            listItemFormatEnabled: false,
            listItemFormatPattern: [],
            isConstraining: false,
          });
          this.modal.hide();
          if (!hideValidationToast) {
            Toast.success(this.props, 'error:valid.saved');
          }
        } catch (error) {
          ErrorUtil.handleCatched(this.props, error);
        } finally {
          release();
          this.setState({ loading: false });
        }
      }
    };

    updateTextConsistency = async () => {
      const { t, methods, hideValidationToast } = this.props;
      const {
        textConstraintType,
        textMinLength,
        textMaxLength,
        textExcludeLimits,
        textAllowLetters,
        textAllowDigits,
        textFixedLength,
        textFormatEnabled,
        textFormatPattern,
        isConstraining,
      } = this.state;

      if (methods.patchElement && !this.mutex.isLocked()) {
        const release = await this.mutex.acquire();
        this.setState({ loading: true });

        try {
          if (textConstraintType === TEXT_CONSTRAINT_NONE) {
            // Pas de contrainte de longueur, seulement les filtres de caractères
            await methods.patchElement({
              text_min_length: null,
              text_max_length: null,
              text_exclude_limits: null,
              text_allow_letters: textAllowLetters,
              text_allow_digits: textAllowDigits,
              text_fixed_length: null,
              text_format_pattern: null,
              is_constraining: isConstraining,
            });
          } else if (textConstraintType === TEXT_CONSTRAINT_MIN_MAX) {
            // Validation Min/Max
            checkTextSettingsValidity(t, textMinLength, textMaxLength);

            const minLength = textMinLength ? Number.parseInt(textMinLength, 10) : null;
            const maxLength = textMaxLength ? Number.parseInt(textMaxLength, 10) : null;

            await methods.patchElement({
              text_min_length: minLength,
              text_max_length: maxLength,
              text_exclude_limits: (minLength && maxLength) ? textExcludeLimits : null,
              text_allow_letters: textAllowLetters,
              text_allow_digits: textAllowDigits,
              text_fixed_length: null,
              text_format_pattern: null,
              is_constraining: isConstraining,
            });
          } else {
            // Validation Longueur fixe
            if (!textFixedLength) {
              throw new Error(t('error:consistency-check.fixed-length-required'));
            }

            const fixedLength = Number.parseInt(textFixedLength, 10);

            if (fixedLength < 1 || fixedLength > MAX_FIXED_LENGTH) {
              throw new Error(t('error:consistency-check.fixed-length-out-of-range'));
            }

            if (textFormatEnabled) {
              if (!textFormatPattern || textFormatPattern.length === 0) {
                throw new Error(t('error:consistency-check.format-pattern-required'));
              }

              if (textFormatPattern.length !== fixedLength) {
                throw new Error(t('error:consistency-check.format-pattern-length-mismatch'));
              }

              if (!this.validateTextPattern(textFormatPattern)) {
                throw new Error(t('error:consistency-check.format-pattern-incomplete'));
              }
            }

            await methods.patchElement({
              text_fixed_length: fixedLength,
              // eslint-disable-next-line max-len
              text_format_pattern: textFormatEnabled
                ? this.buildRegexFromPattern(textFormatPattern)
                : null,
              text_min_length: null,
              text_max_length: null,
              text_exclude_limits: null,
              text_allow_letters: textAllowLetters,
              text_allow_digits: textAllowDigits,
              is_constraining: isConstraining,
            });
          }

          this.modal.hide();
          if (!hideValidationToast) {
            Toast.success(this.props, 'error:valid.saved');
          }
        } catch (error) {
          ErrorUtil.handleCatched(this.props, error);
        } finally {
          release();
          this.setState({ loading: false });
        }
      }
    };

    updateListConsistency = async () => {
      const { t, methods, hideValidationToast } = this.props;
      const {
        listConstraintType,
        listItemMinLength,
        listItemMaxLength,
        listItemExcludeLimits,
        listItemAllowLetters,
        listItemAllowDigits,
        listItemFixedLength,
        listItemFormatEnabled,
        listItemFormatPattern,
        isConstraining,
      } = this.state;

      if (methods.patchElement && !this.mutex.isLocked()) {
        const release = await this.mutex.acquire();
        this.setState({ loading: true });

        try {
          if (listConstraintType === TEXT_CONSTRAINT_NONE) {
            // Pas de contrainte de longueur, seulement les filtres de caractères
            await methods.patchElement({
              list_item_min_length: null,
              list_item_max_length: null,
              list_item_exclude_limits: null,
              list_item_allow_letters: listItemAllowLetters,
              list_item_allow_digits: listItemAllowDigits,
              list_item_fixed_length: null,
              list_item_format_pattern: null,
              is_constraining: isConstraining,
            });
          } else if (listConstraintType === TEXT_CONSTRAINT_MIN_MAX) {
            // Validation Min/Max
            checkTextSettingsValidity(t, listItemMinLength, listItemMaxLength);

            const itemMinLen = listItemMinLength ? Number.parseInt(listItemMinLength, 10) : null;
            const itemMaxLen = listItemMaxLength ? Number.parseInt(listItemMaxLength, 10) : null;

            await methods.patchElement({
              list_item_min_length: itemMinLen,
              list_item_max_length: itemMaxLen,
              list_item_exclude_limits: (itemMinLen && itemMaxLen) ? listItemExcludeLimits : null,
              list_item_allow_letters: listItemAllowLetters,
              list_item_allow_digits: listItemAllowDigits,
              list_item_fixed_length: null,
              list_item_format_pattern: null,
              is_constraining: isConstraining,
            });
          } else {
            // Validation Longueur fixe
            if (!listItemFixedLength) {
              throw new Error(t('error:consistency-check.fixed-length-required'));
            }

            const fixedLength = Number.parseInt(listItemFixedLength, 10);

            if (fixedLength < 1 || fixedLength > MAX_FIXED_LENGTH) {
              throw new Error(t('error:consistency-check.fixed-length-out-of-range'));
            }

            if (listItemFormatEnabled) {
              if (!listItemFormatPattern || listItemFormatPattern.length === 0) {
                throw new Error(t('error:consistency-check.format-pattern-required'));
              }

              if (listItemFormatPattern.length !== fixedLength) {
                throw new Error(t('error:consistency-check.format-pattern-length-mismatch'));
              }

              if (!this.validateTextPattern(listItemFormatPattern)) {
                throw new Error(t('error:consistency-check.format-pattern-incomplete'));
              }
            }

            await methods.patchElement({
              list_item_fixed_length: fixedLength,
              list_item_format_pattern: listItemFormatEnabled
                ? this.buildRegexFromPattern(listItemFormatPattern)
                : null,
              list_item_min_length: null,
              list_item_max_length: null,
              list_item_exclude_limits: null,
              list_item_allow_letters: listItemAllowLetters,
              list_item_allow_digits: listItemAllowDigits,
              is_constraining: isConstraining,
            });
          }

          this.modal.hide();
          if (!hideValidationToast) {
            Toast.success(this.props, 'error:valid.saved');
          }
        } catch (error) {
          ErrorUtil.handleCatched(this.props, error);
        } finally {
          release();
          this.setState({ loading: false });
        }
      }
    };

    renderListCheck = () => {
      const { t, elementName } = this.props;
      const {
        listConstraintType,
        listItemMinLength,
        listItemMaxLength,
        listItemExcludeLimits,
        listItemAllowLetters,
        listItemAllowDigits,
        listItemFixedLength,
        listItemFormatEnabled,
        listItemFormatPattern,
        isConstraining,
      } = this.state;

      const listConstraintTypeChoices = [
        {
          text: t('project:consistency-check.length-min-max'),
          value: TEXT_CONSTRAINT_MIN_MAX,
        },
        {
          text: t('project:consistency-check.length-fixed'),
          value: TEXT_CONSTRAINT_FIXED,
        },
      ];

      const lengthRuleEnabled = listConstraintType !== TEXT_CONSTRAINT_NONE;

      return (
        <div className="pb-3">
          {/* Titre */}
          <div className="mb-3">
            <label className="form-label text-primary">
              {t('project:consistency-check.set-validity-parameters')}
            </label>
          </div>

          {/* Checkboxes de caractères autorisés */}
          <div className="mb-2">
            <Checkbox
              id="list-item-allow-letters"
              checked={listItemAllowLetters}
              disabled={listItemFormatEnabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                // If unchecking and digits is also unchecked, force digits on
                if (!newValue && !listItemAllowDigits) {
                  this.setState({ listItemAllowLetters: false, listItemAllowDigits: true });
                } else {
                  this.setState({ listItemAllowLetters: newValue });
                }
              }}
            >
              {t('project:consistency-check.allow-letters')}
            </Checkbox>
          </div>

          <div className="mb-2">
            <Checkbox
              id="list-item-allow-digits"
              checked={listItemAllowDigits}
              disabled={listItemFormatEnabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                // If unchecking and letters is also unchecked, force letters on
                if (!newValue && !listItemAllowLetters) {
                  this.setState({ listItemAllowDigits: false, listItemAllowLetters: true });
                } else {
                  this.setState({ listItemAllowDigits: newValue });
                }
              }}
            >
              {t('project:consistency-check.allow-digits')}
            </Checkbox>
          </div>

          {/* Checkbox pour activer la règle de longueur avec radio buttons inline */}
          <div className="mb-2 d-flex align-items-center flex-wrap">
            <Checkbox
              id="list-length-rule-enabled"
              checked={lengthRuleEnabled}
              onChange={(e) => {
                if (e.target.checked) {
                  // Default to min/max when enabling
                  this.setState({ listConstraintType: TEXT_CONSTRAINT_MIN_MAX });
                } else {
                  // Reset format code when disabling length rule
                  this.setState({
                    listConstraintType: TEXT_CONSTRAINT_NONE,
                    listItemFormatEnabled: false,
                  });
                }
              }}
            >
              {t('project:consistency-check.enable-length-rule')}
            </Checkbox>
            {lengthRuleEnabled && (
              <div className="d-flex align-items-center ml-2">
                {listConstraintTypeChoices.map((choice) => (
                  <div key={choice.value} className="form-check form-check-inline mb-0">
                    <input
                      type="radio"
                      id={`list-constraint-${choice.value}`}
                      name="list-constraint-type"
                      className="form-check-input"
                      value={choice.value}
                      checked={listConstraintType === choice.value}
                      onChange={(e) => {
                        const newType = e.target.value;
                        // Reset format when switching away from FIXED
                        if (newType !== TEXT_CONSTRAINT_FIXED) {
                          this.setState({
                            listConstraintType: newType,
                            listItemFormatEnabled: false,
                          });
                        } else {
                          this.setState({ listConstraintType: newType });
                        }
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`list-constraint-${choice.value}`}
                    >
                      {choice.text}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mode Min/Max */}
          {listConstraintType === TEXT_CONSTRAINT_MIN_MAX && (
            <div className="row ml-4">
              <div className="col-12 mt-3">
                <small className="text-muted font-italic">
                  {t('project:consistency-check.list-item-min-length-help')}
                </small>
              </div>

              <div className="col-12 mb-2 d-flex align-items-center">
                <input
                  type="number"
                  name={`${elementName}-list-item-min-length`}
                  className="form-control"
                  style={{ width: '150px' }}
                  placeholder={t('project:consistency-check.min-value-placeholder')}
                  value={listItemMinLength}
                  onChange={(e) => {
                    this.setState({ listItemMinLength: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (['-', '.', ',', 'e', 'E'].includes(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                    return true;
                  }}
                />
                <small className="text-orange font-italic ml-2">
                  {t('project:consistency-check.spaces-included')}
                </small>
              </div>

              <div className="col-12 mt-3">
                <small className="text-muted font-italic">
                  {t('project:consistency-check.list-item-max-length-help')}
                </small>
              </div>

              <div className="col-12 mb-2 d-flex align-items-center">
                <input
                  type="number"
                  name={`${elementName}-list-item-max-length`}
                  className="form-control"
                  style={{ width: '150px' }}
                  placeholder={t('project:consistency-check.max-value-placeholder')}
                  value={listItemMaxLength}
                  onChange={(e) => {
                    this.setState({ listItemMaxLength: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (['-', '.', ',', 'e', 'E'].includes(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                    return true;
                  }}
                />
                <small className="text-orange font-italic ml-2">
                  {t('project:consistency-check.spaces-included')}
                </small>
              </div>

              <div className="col-12 mb-2">
                <Checkbox
                  id="list-item-exclude-limits"
                  checked={listItemExcludeLimits}
                  onChange={(e) => {
                    this.setState({ listItemExcludeLimits: e.target.checked });
                  }}
                  disabled={!(listItemMinLength && listItemMaxLength)}
                >
                  {t('project:consistency-check.exclude-limit-values')}
                </Checkbox>
              </div>
            </div>
          )}

          {/* Mode Longueur fixe */}
          {listConstraintType === TEXT_CONSTRAINT_FIXED && (
            <div className="row ml-4">
              <div className="col-12 mb-3 d-flex align-items-center">
                <small className="text-muted font-italic mr-2">
                  {t('project:consistency-check.fixed-length-label')}
                </small>
                <input
                  type="number"
                  name={`${elementName}-list-item-fixed-length`}
                  className="form-control form-control-sm"
                  style={{ width: '70px' }}
                  placeholder=""
                  value={listItemFixedLength}
                  min="1"
                  max={MAX_FIXED_LENGTH}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const numValue = Number.parseInt(newValue, 10);

                    if (!Number.isNaN(numValue) && (numValue > MAX_FIXED_LENGTH || numValue < 1)) {
                      return;
                    }

                    this.setState({ listItemFixedLength: newValue });
                    if (listItemFormatEnabled) {
                      this.updateListItemFormatPatternLength(newValue);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (['-', '.', ',', 'e', 'E'].includes(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                    return true;
                  }}
                />
              </div>

              <div className="col-12 mb-3 d-flex align-items-center">
                <Checkbox
                  id="list-item-format-enabled"
                  checked={listItemFormatEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    this.setState({ listItemFormatEnabled: enabled });
                    if (enabled && listItemFixedLength) {
                      this.updateListItemFormatPatternLength(listItemFixedLength);
                    }
                  }}
                  disabled={!listItemFixedLength}
                >
                  {t('project:consistency-check.specify-code-format')}
                </Checkbox>
                <Help iconClassName="ml-2" interactive>
                  {t('project:consistency-check.code-format-help')}
                  <br />
                  <FAQLink
                    articleId={17}
                    text="project:consistency-check.code-format-help-link"
                    linkClassName="classical-link text-newblue-1"
                  />
                </Help>
              </div>

                {listItemFormatEnabled && listItemFormatPattern.length > 0 && (
                <div className="col-12">
                  <small className="text-muted font-italic mb-2 d-block">
                    {t('project:consistency-check.select-format-for-each-position')}
                  </small>
                  <div className="d-flex flex-wrap" style={{ marginTop: '8px' }}>
                    {listItemFormatPattern.map((pattern, index) => {
                      const formatOptions = getFormatOptions(t);
                      const patternValue = formatOptions.find((opt) => opt.value === pattern);
                      return (
                        <div style={{ flexShrink: 0 }}>
                          <Select
                            className="react-select format-pattern-select"
                            classNamePrefix="react-select"
                            options={formatOptions}
                            value={patternValue}
                            onChange={
                            // eslint-disable-next-line max-len
                            (selectedOption) => this.handleListItemFormatPatternChange(index, selectedOption)
                          }
                            placeholder=""
                            menuPlacement="auto"
                            formatOptionLabel={formatOptionLabel}
                            styles={formatPatternSelectStyles}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                )}
            </div>
          )}

          {/* Constraining checkbox */}
          <hr className="my-3" />
          <div className="d-flex align-items-center">
            <Checkbox
              id="list-is-constraining"
              checked={isConstraining}
              onChange={(e) => this.setState({ isConstraining: e.target.checked })}
              color="danger"
            >
              {t('project:consistency-check.is-constraining')}
            </Checkbox>
            <Help iconClassName="ml-2">
              {t('project:consistency-check.is-constraining-tooltip')}
            </Help>
          </div>
          <div className="mt-1">
            <small className="font-italic text-orange">
              {t('project:consistency-check.is-constraining-description')}
            </small>
          </div>

        </div>
      );
    };

    deleteTextSettings = async () => {
      const { methods, hideValidationToast } = this.props;
      if (methods.patchElement && !this.mutex.isLocked()) {
        const release = await this.mutex.acquire();
        this.setState({ loading: true });
        try {
          await methods.patchElement({
            text_min_length: null,
            text_max_length: null,
            text_exclude_limits: null,
            text_allow_letters: null,
            text_allow_digits: null,
            text_fixed_length: null,
            text_format_pattern: null,
            is_constraining: null,
          });
          this.setState({
            textMinLength: '',
            textMaxLength: '',
            textExcludeLimits: false,
            textAllowLetters: true,
            textAllowDigits: true,
            textFixedLength: '',
            textFormatEnabled: false,
            textFormatPattern: [],
            isConstraining: false,
          });
          this.modal.hide();
          if (!hideValidationToast) {
            Toast.success(this.props, 'error:valid.saved');
          }
        } catch (error) {
          ErrorUtil.handleCatched(this.props, error);
        } finally {
          release();
          this.setState({ loading: false });
        }
      }
    };

    handleFormatPatternChange = (index, selectedOption) => {
      const { textFormatPattern } = this.state;
      const newPattern = [...textFormatPattern];
      // Store only the value, not the whole object
      newPattern[index] = selectedOption ? selectedOption.value : null;
      this.setState({ textFormatPattern: newPattern });
    };

    updateFormatPatternLength = (length) => {
      const { textFormatPattern } = this.state;
      const newLength = Number.parseInt(length, 10);

      if (!Number.isNaN(newLength) && newLength > 0) {
        const maxLength = Math.min(newLength, MAX_FIXED_LENGTH);
        // eslint-disable-next-line max-len
        const newPattern = Array(maxLength).fill(null).map((_, i) => textFormatPattern[i] || FORMAT_UPPERCASE);
        this.setState({ textFormatPattern: newPattern });
      } else {
        this.setState({ textFormatPattern: [] });
      }
    };

    buildRegexFromPattern = (patternArray) => {
      if (!patternArray || patternArray.length === 0) {
        return null;
      }

      const regexMap = {
        A: '[A-Z]', // Lettre majuscule
        a: '[a-z]', // Lettre minuscule
        1: '[0-9]', // Chiffre
        '-': '-', // Tiret
        _: '_', // Underscore
        '/': '\\/', // Slash
      };

      const regexParts = patternArray.map((item) => {
        const value = item?.value || item;
        return regexMap[value] || '';
      });

      return `^${regexParts.join('')}$`;
    };

    renderTextCheck = () => {
      const { t, elementName } = this.props;
      const {
        textConstraintType,
        textMinLength,
        textMaxLength,
        textExcludeLimits,
        textAllowLetters,
        textAllowDigits,
        textFixedLength,
        textFormatEnabled,
        textFormatPattern,
        isConstraining,
      } = this.state;

      const textConstraintTypeChoices = [
        {
          text: t('project:consistency-check.length-min-max'),
          value: TEXT_CONSTRAINT_MIN_MAX,
        },
        {
          text: t('project:consistency-check.length-fixed'),
          value: TEXT_CONSTRAINT_FIXED,
        },
      ];

      const lengthRuleEnabled = textConstraintType !== TEXT_CONSTRAINT_NONE;

      return (
        <div className="pb-3">
          {/* Titre */}
          <div className="mb-3">
            <label className="form-label text-primary">
              {t('project:consistency-check.set-validity-parameters')}
            </label>
          </div>

          {/* Checkboxes de caractères autorisés */}
          <div className="mb-2">
            <Checkbox
              id="text-allow-letters"
              checked={textAllowLetters}
              disabled={textFormatEnabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                // If unchecking and digits is also unchecked, force digits on
                if (!newValue && !textAllowDigits) {
                  this.setState({ textAllowLetters: false, textAllowDigits: true });
                } else {
                  this.setState({ textAllowLetters: newValue });
                }
              }}
            >
              {t('project:consistency-check.allow-letters')}
            </Checkbox>
          </div>

          <div className="mb-2">
            <Checkbox
              id="text-allow-digits"
              checked={textAllowDigits}
              disabled={textFormatEnabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                // If unchecking and letters is also unchecked, force letters on
                if (!newValue && !textAllowLetters) {
                  this.setState({ textAllowDigits: false, textAllowLetters: true });
                } else {
                  this.setState({ textAllowDigits: newValue });
                }
              }}
            >
              {t('project:consistency-check.allow-digits')}
            </Checkbox>
          </div>

          {/* Checkbox pour activer la règle de longueur avec radio buttons inline */}
          <div className="mb-2 d-flex align-items-center flex-wrap">
            <Checkbox
              id="text-length-rule-enabled"
              checked={lengthRuleEnabled}
              onChange={(e) => {
                if (e.target.checked) {
                  // Default to min/max when enabling
                  this.setState({ textConstraintType: TEXT_CONSTRAINT_MIN_MAX });
                } else {
                  // Reset format code when disabling length rule
                  this.setState({
                    textConstraintType: TEXT_CONSTRAINT_NONE,
                    textFormatEnabled: false,
                  });
                }
              }}
            >
              {t('project:consistency-check.enable-length-rule')}
            </Checkbox>
            {lengthRuleEnabled && (
              <div className="d-flex align-items-center ml-2">
                {textConstraintTypeChoices.map((choice) => (
                  <div key={choice.value} className="form-check form-check-inline mb-0">
                    <input
                      type="radio"
                      id={`text-constraint-${choice.value}`}
                      name="text-constraint-type"
                      className="form-check-input"
                      value={choice.value}
                      checked={textConstraintType === choice.value}
                      onChange={(e) => {
                        const newType = e.target.value;
                        // Reset format when switching away from FIXED
                        if (newType !== TEXT_CONSTRAINT_FIXED) {
                          this.setState({ textConstraintType: newType, textFormatEnabled: false });
                        } else {
                          this.setState({ textConstraintType: newType });
                        }
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`text-constraint-${choice.value}`}
                    >
                      {choice.text}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mode Min/Max */}
          {textConstraintType === TEXT_CONSTRAINT_MIN_MAX && (
            <div className="row ml-4">
              <div className="col-12 mt-3">
                <small className="text-muted font-italic">
                  {t('project:consistency-check.text-min-length-help')}
                </small>
              </div>

              <div className="col-12 mb-2 d-flex align-items-center">
                <input
                  type="number"
                  name={`${elementName}-text-minimum-length`}
                  className="form-control"
                  style={{ width: '150px' }}
                  placeholder={t('project:consistency-check.min-value-placeholder')}
                  value={textMinLength}
                  onChange={(e) => {
                    this.setState({ textMinLength: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (['-', '.', ',', 'e', 'E'].includes(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                    return true;
                  }}
                />
                <small className="text-orange font-italic ml-2">
                  {t('project:consistency-check.spaces-included')}
                </small>
              </div>

              <div className="col-12 mt-3">
                <small className="text-muted font-italic">
                  {t('project:consistency-check.text-max-length-help')}
                </small>
              </div>

              <div className="col-12 mb-2 d-flex align-items-center">
                <input
                  type="number"
                  name={`${elementName}-text-maximum-length`}
                  className="form-control"
                  style={{ width: '150px' }}
                  placeholder={t('project:consistency-check.max-value-placeholder')}
                  value={textMaxLength}
                  onChange={(e) => {
                    this.setState({ textMaxLength: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (['-', '.', ',', 'e', 'E'].includes(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                    return true;
                  }}
                />
                <small className="text-orange font-italic ml-2">
                  {t('project:consistency-check.spaces-included')}
                </small>
              </div>

              <div className="col-12 mb-2">
                <Checkbox
                  id="text-exclude-limits"
                  checked={textExcludeLimits}
                  onChange={(e) => {
                    this.setState({ textExcludeLimits: e.target.checked });
                  }}
                  disabled={!(textMinLength && textMaxLength)}
                >
                  {t('project:consistency-check.exclude-limit-values')}
                </Checkbox>
              </div>
            </div>
          )}

          {/* Mode Longueur fixe */}
          {textConstraintType === TEXT_CONSTRAINT_FIXED && (
          <div className="row ml-4">
            <div className="col-12 mb-3 d-flex align-items-center">
              <small className="text-muted font-italic mr-2">
                {t('project:consistency-check.fixed-length-label')}
              </small>
              <input
                type="number"
                name={`${elementName}-text-fixed-length`}
                className="form-control form-control-sm"
                style={{ width: '70px' }}
                placeholder=""
                value={textFixedLength}
                min="1"
                max={MAX_FIXED_LENGTH}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const numValue = Number.parseInt(newValue, 10);

                  if (!Number.isNaN(numValue) && (numValue > MAX_FIXED_LENGTH || numValue < 1)) {
                    return;
                  }

                  this.setState({ textFixedLength: newValue });
                  if (textFormatEnabled) {
                    this.updateFormatPatternLength(newValue);
                  }
                }}
                onKeyPress={(e) => {
                  if (['-', '.', ',', 'e', 'E'].includes(e.key)) {
                    e.preventDefault();
                    return false;
                  }
                  return true;
                }}
              />
            </div>

            <div className="col-12 mb-3 d-flex align-items-center">
              <Checkbox
                id="text-format-enabled"
                checked={textFormatEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  this.setState({ textFormatEnabled: enabled });
                  if (enabled && textFixedLength) {
                    this.updateFormatPatternLength(textFixedLength);
                  }
                }}
                disabled={!textFixedLength}
              >
                {t('project:consistency-check.specify-code-format')}
              </Checkbox>
              <Help iconClassName="ml-2" interactive>
                {t('project:consistency-check.code-format-help')}
                <br />
                <FAQLink
                  articleId={17}
                  text="project:consistency-check.code-format-help-link"
                  linkClassName="classical-link text-newblue-1"
                />
              </Help>
            </div>

              {textFormatEnabled && textFormatPattern.length > 0 && (
              <div className="col-12">
                <small className="text-muted font-italic mb-2 d-block">
                  {t('project:consistency-check.select-format-for-each-position')}
                </small>
                <div className="d-flex flex-wrap" style={{ marginTop: '8px' }}>
                  {textFormatPattern.map((pattern, index) => {
                    const formatOptions = getFormatOptions(t);
                    const patternValue = formatOptions.find((opt) => opt.value === pattern);
                    return (
                      <div style={{ flexShrink: 0 }}>
                        <Select
                          className="react-select format-pattern-select"
                          classNamePrefix="react-select"
                          options={formatOptions}
                          value={patternValue}
                          onChange={
                          (selectedOption) => this.handleFormatPatternChange(index, selectedOption)
                        }
                          placeholder=""
                          menuPlacement="auto"
                          formatOptionLabel={formatOptionLabel}
                          styles={formatPatternSelectStyles}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              )}
          </div>
          )}

          {/* Constraining checkbox */}
          <hr className="my-3" />
          <div className="d-flex align-items-center">
            <Checkbox
              id="text-is-constraining"
              checked={isConstraining}
              onChange={(e) => this.setState({ isConstraining: e.target.checked })}
              color="danger"
            >
              {t('project:consistency-check.is-constraining')}
            </Checkbox>
            <Help iconClassName="ml-2">
              {t('project:consistency-check.is-constraining-tooltip')}
            </Help>
          </div>
          <div className="mt-1">
            <small className="font-italic text-orange">
              {t('project:consistency-check.is-constraining-description')}
            </small>
          </div>

        </div>
      );
    };

    handleListItemFormatPatternChange = (index, selectedOption) => {
      const { listItemFormatPattern } = this.state;
      const newPattern = [...listItemFormatPattern];
      // Store only the value, not the whole object
      newPattern[index] = selectedOption ? selectedOption.value : null;
      this.setState({ listItemFormatPattern: newPattern });
    };

    updateListItemFormatPatternLength = (length) => {
      const { listItemFormatPattern } = this.state;
      const newLength = Number.parseInt(length, 10);

      if (!Number.isNaN(newLength) && newLength > 0) {
        const maxLength = Math.min(newLength, MAX_FIXED_LENGTH);
        // eslint-disable-next-line max-len
        const newPattern = Array(maxLength).fill(null).map((_, i) => listItemFormatPattern[i] || FORMAT_UPPERCASE);
        this.setState({ listItemFormatPattern: newPattern });
      } else {
        this.setState({ listItemFormatPattern: [] });
      }
    };

    preventIncorrectChars = (e) => {
      if (!['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '.', ',', '-'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Workaround for dot issue in french locale in Firefox (only comma is
      // working so don't add it and inform the user)
      // See https://bugzilla.mozilla.org/show_bug.cgi?id=1253606
      // Function from ElementMeasurement.jsx
      if (this.props.user.language === 'fr' && e.key === '.') {
      // Try to detect Firefox to not alter right behaviour of others browers
      // (Safari and Chrome)
        if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
          e.preventDefault();
          e.stopPropagation();
          Toast.error(this.props, 'error:error.use-comma-only-for-float');
          return false;
        }
      }

      return true;
    }

  updateConsistency = async () => {
    const { t, methods, hideValidationToast } = this.props;
    const {
      lowBound, highBound, excludeLimits, forbidDecimals, typeOfSettings, isConstraining,
    } = this.state;
    if (methods.patchElement && !this.mutex.isLocked()) {
      const release = await this.mutex.acquire();
      this.setState({ loading: true });
      const min = lowBound || null;
      const max = highBound || null;
      const integer = forbidDecimals || null;
      // When max and min are null, we don't need strict
      const strict = (min || max) ? excludeLimits : null;
      try {
        this.memoizedCheckSettingsValidity(
          t, lowBound, highBound, forbidDecimals || false, typeOfSettings === SPECIFIC_VALUE,
        );
        await methods.patchElement({
          min,
          max,
          strict,
          integer,
          is_constraining: isConstraining,
        });
        this.modal.hide();
        if (!hideValidationToast) {
          Toast.success(this.props, 'error:valid.saved');
        }
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      } finally {
        release();
        this.setState({ loading: false });
      }
    }
  }

  updateDateConsistency = async () => {
    const {
      t, methods, hideValidationToast,
    } = this.props;
    const {
      before, after, lessThanIncDate, greaterThanIncDate, beforeVar, afterVar,
      typeOfSettings, truncMin, truncMax, isConstraining,
    } = this.state;
    const minDate = after ? moment(after) : null;
    const maxDate = before ? moment(before) : null;
    const isIncDate = lessThanIncDate && greaterThanIncDate
      && lessThanIncDate === greaterThanIncDate;
    const isAVariable = beforeVar && afterVar && beforeVar === afterVar;

    if (methods.patchElement && !this.mutex.isLocked()) {
      const release = await this.mutex.acquire();
      this.setState({ loading: true });
      try {
        checkDateValidity(
          t, minDate, maxDate, isIncDate, isAVariable,
          typeOfSettings === SPECIFIC_DATE,
        );
        await methods.patchElement({
          before: maxDate ? maxDate.format(API_DATE_TIME_FORMAT) : null,
          after: minDate ? minDate.format(API_DATE_TIME_FORMAT) : null,
          before_var: beforeVar,
          after_var: afterVar,
          less_than_inc_date: lessThanIncDate || null,
          greater_than_inc_date: greaterThanIncDate || null,
          trunc_min: truncMin || null,
          trunc_max: truncMax || null,
          is_constraining: isConstraining,
        });
        this.modal.hide();
        if (!hideValidationToast) {
          Toast.success(this.props, 'error:valid.saved');
        }
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error);
      } finally {
        release();
        this.setState({ loading: false });
      }
    }
  }

  getMin = () => {
    const { element } = this.props;
    return (element.min !== undefined) ? element.min.toString() : '';
  }

  getMax = () => {
    const { element } = this.props;
    return (element.max !== undefined) ? element.max.toString() : '';
  }

  getCalculationExcludeLimits = (typeOfSettings) => {
    const { element } = this.props;
    if (typeOfSettings === SPECIFIC_VALUE) {
      return false;
    }
    return element.strict || false;
  };

  renderQuantitativeCheck = () => {
    const { elementName, t } = this.props;
    const {
      excludeLimits, forbidDecimals, lowBound, highBound, isConstraining,
    } = this.state;

    return (
      <div className="row pb-3">
        <LimitValueField
          label={t('project:consistency-check.minimum')}
          labelClassName="small font-italic m-0 pb-1"
          name={`${elementName}-minimum`}
          type="number"
          placeholder={t('project:consistency-check.minimum-short')}
          onChange={(e) => {
            this.setState({ lowBound: e.target.value });
          }}
          defaultValue={this.getMin()}
          className="col-12"
          onKeyPress={this.preventIncorrectChars}
        />
        <LimitValueField
          label={t('project:consistency-check.maximum')}
          labelClassName="small font-italic m-0 pb-1"
          name={`${elementName}-maximum`}
          type="number"
          placeholder={t('project:consistency-check.maximum-short')}
          onChange={(e) => {
            this.setState({ highBound: e.target.value });
          }}
          defaultValue={this.getMax()}
          className="col-12 py-2"
          onKeyPress={this.preventIncorrectChars}
        />
        <div className="col-12 pt-3 pb-2">
          <Checkbox
            id="exclude-limits"
            checked={excludeLimits || false}
            onChange={(e) => {
              this.setState({ excludeLimits: e.target.checked });
            }}
            disabled={!(lowBound || highBound)}
          >
            {t('project:consistency-check.exclude-bounds')}
          </Checkbox>
        </div>
        <div className="col-12">
          <Checkbox
            id="do-not-allow-decimals"
            checked={forbidDecimals || false}
            onChange={(e) => {
              this.setState({ forbidDecimals: e.target.checked });
            }}
          >
            {t('project:consistency-check.forbid-decimals')}
          </Checkbox>
        </div>

        {/* Constraining checkbox */}
        <div className="col-12">
          <hr className="my-3" />
          <div className="d-flex align-items-center">
            <Checkbox
              checked={isConstraining}
              onChange={(e) => this.setState({ isConstraining: e.target.checked })}
              color="danger"
            >
              {t('project:consistency-check.is-constraining')}
            </Checkbox>
            <Help iconClassName="ml-2">
              {t('project:consistency-check.is-constraining-tooltip')}
            </Help>
          </div>
          <div className="mt-1">
            <small className="font-italic text-orange">
              {t('project:consistency-check.is-constraining-description')}
            </small>
          </div>
        </div>
      </div>
    );
  }

  renderCalculationCheck = () => {
    const { t, elementName } = this.props;
    const {
      typeOfSettings, lowBound, highBound, excludeLimits, isConstraining,
    } = this.state;

    const defaultValues = { value: '', min: '', max: '' };
    const defaultTypeOfSettings = this.getCalculationTypeOfSettings(typeOfSettings);
    if (defaultTypeOfSettings === SPECIFIC_VALUE) {
      defaultValues.value = this.getMin();
    } else if (defaultTypeOfSettings === INTERVAL) {
      defaultValues.min = this.getMin();
      defaultValues.max = this.getMax();
    }

    const calculationTypeChoices = [
      {
        text: t('project:consistency-check.interval'),
        value: INTERVAL,
      },
      {
        text: t('project:consistency-check.a-specific-value'),
        value: SPECIFIC_VALUE,
      },
    ];

    return (
      <div>
        <div>
          <LabeledChoice
            name="calculation-type-of-control"
            label={t('project:consistency-check.response-must-be')}
            value={typeOfSettings}
            onChange={(e) => {
              const newTypeOfSettings = e.target.value;
              this.setState({
                typeOfSettings: newTypeOfSettings,
                lowBound: (newTypeOfSettings === defaultTypeOfSettings) ? this.getMin() : '',
                highBound: (newTypeOfSettings === defaultTypeOfSettings) ? this.getMax() : '',
                excludeLimits: this.getCalculationExcludeLimits(newTypeOfSettings),
              });
            }}
            choices={calculationTypeChoices}
            hideOptionalLabel
            colLabelClassName="col-12 mb-2"
          />
        </div>
        {
          (typeOfSettings === SPECIFIC_VALUE) && (
            <div className="py-3">
              <div className="mb-2">
                {t('project:consistency-check.specific-value')}
              </div>
              <LimitValueField
                name={`${elementName}-specific-value`}
                type="number"
                onChange={(e) => {
                  this.setState({ lowBound: e.target.value, highBound: e.target.value });
                }}
                defaultValue={defaultValues.value}
                placeholder={t('project:consistency-check.value')}
                onKeyPress={this.preventIncorrectChars}
                className="pt-2"
              />
            </div>
          )
        }
        {
          (typeOfSettings === INTERVAL) && (
            <div className="pt-3">
              <div className="pb-2 row align-items-center">
                <div className="col-auto pr-1">
                  {t('project:consistency-check.limit-values')}
                </div>
                <div className="col-auto px-0">
                  <small className="font-italic ml-1">
                    {t('project:consistency-check.at-least-one-limit')}
                  </small>
                </div>
              </div>
              <LimitValueField
                name={`${elementName}-minimum`}
                type="number"
                onChange={(e) => {
                  this.setState({ lowBound: e.target.value });
                }}
                defaultValue={defaultValues.min}
                placeholder={t('project:consistency-check.minimum-short')}
                className="pt-2"
                onKeyPress={this.preventIncorrectChars}
                label={t('project:consistency-check.minimum')}
                labelClassName="small font-italic m-0 pb-1"
              />
              <LimitValueField
                name={`${elementName}-maximum`}
                type="number"
                onChange={(e) => {
                  this.setState({ highBound: e.target.value });
                }}
                defaultValue={defaultValues.max}
                placeholder={t('project:consistency-check.maximum-short')}
                className="py-2"
                onKeyPress={this.preventIncorrectChars}
                label={t('project:consistency-check.maximum')}
                labelClassName="small font-italic m-0 pb-1"
              />
            </div>
          )
        }
        {
          (typeOfSettings === INTERVAL) && (
            <div className="py-3">
              <Checkbox
                id="exclude-limits"
                checked={excludeLimits || false}
                onChange={(e) => {
                  this.setState({ excludeLimits: e.target.checked });
                }}
                disabled={!(lowBound || highBound)}
                className="col-auto"
              >
                {t('project:consistency-check.exclude-bounds')}
              </Checkbox>
            </div>
          )
        }

        {/* Constraining checkbox */}
        <hr className="my-3" />
        <div className="d-flex align-items-center">
          <Checkbox
            checked={isConstraining}
            onChange={(e) => this.setState({ isConstraining: e.target.checked })}
            color="danger"
          >
            {t('project:consistency-check.is-constraining')}
          </Checkbox>
          <Help iconClassName="ml-2">
            {t('project:consistency-check.is-constraining-tooltip')}
          </Help>
        </div>
        <div className="mt-1">
          <small className="font-italic text-orange">
            {t('project:consistency-check.is-constraining-description')}
          </small>
        </div>
      </div>
    );
  }

  onDateChange = (date, dateType, value, truncTo = null) => {
    const truncValue = {};

    if (date === 'after') {
      if (dateType === CUSTOM_DATE) {
        this.setState({
          after: value,
          afterVar: null,
          greaterThanIncDate: false,
        });
      } else if (dateType === FORM_DATE) {
        if (truncTo) {
          truncValue.truncMin = truncTo;
        }
        this.setState({
          after: null,
          afterVar: value,
          greaterThanIncDate: false,
          ...truncValue,
        });
      }
    } else if (date === 'before') {
      if (dateType === CUSTOM_DATE) {
        this.setState({
          before: value,
          beforeVar: null,
          lessThanIncDate: false,
        });
      } else if (dateType === FORM_DATE) {
        if (truncTo) {
          truncValue.truncMax = truncTo;
        }
        this.setState({
          before: null,
          beforeVar: value,
          lessThanIncDate: false,
          ...truncValue,
        });
      }
    } else if (date === SPECIFIC_DATE) {
      if (dateType === CUSTOM_DATE) {
        this.setState({
          before: value,
          after: value,
          beforeVar: null,
          afterVar: null,
          lessThanIncDate: false,
          greaterThanIncDate: false,
        });
      } else if (dateType === FORM_DATE) {
        if (truncTo) {
          truncValue.truncMin = truncTo;
          truncValue.truncMax = truncTo;
        }
        this.setState({
          before: null,
          after: null,
          beforeVar: value,
          afterVar: value,
          lessThanIncDate: false,
          greaterThanIncDate: false,
          ...truncValue,
        });
      }
    }
  }

  onDateTypeChange = (date, newDateType) => {
    const { element } = this.props;
    const specificDate = element.before && element.after && element.before === element.after;
    const specificFormDate = element.before_var && element.after_var
      && element.before_var === element.after_var;

    if (date === 'after') {
      if (newDateType === CUSTOM_DATE) {
        this.setState({
          after: !specificDate ? element.after : null,
          afterVar: null,
          greaterThanIncDate: null,
          truncMin: null,
        });
      } else if (newDateType === INCLUSION_CREATION_DATE) {
        this.setState({
          after: null,
          afterVar: null,
          greaterThanIncDate: true,
          truncMin: null,
        });
      } else {
        this.setState({
          after: null,
          afterVar: !specificFormDate ? element.after_var : null,
          greaterThanIncDate: null,
          truncMin: !specificFormDate ? element.trunc_min : null,
        });
      }
    } else if (date === 'before') {
      if (newDateType === CUSTOM_DATE) {
        this.setState({
          before: !specificDate ? element.before : null,
          beforeVar: null,
          lessThanIncDate: null,
          truncMax: null,
        });
      } else if (newDateType === INCLUSION_CREATION_DATE) {
        this.setState({
          before: null,
          beforeVar: null,
          lessThanIncDate: true,
          truncMax: null,
        });
      } else {
        this.setState({
          before: null,
          beforeVar: !specificFormDate ? element.before_var : null,
          lessThanIncDate: null,
          truncMax: !specificFormDate ? element.trunc_max : null,
        });
      }
    } else if (date === SPECIFIC_DATE) {
      if (newDateType === CUSTOM_DATE) {
        this.setState({
          before: specificDate ? element.before : null,
          after: specificDate ? element.after : null,
          beforeVar: null,
          afterVar: null,
          greaterThanIncDate: null,
          lessThanIncDate: null,
          truncMin: null,
          truncMax: null,
        });
      } else if (newDateType === INCLUSION_CREATION_DATE) {
        this.setState({
          before: null,
          after: null,
          beforeVar: null,
          afterVar: null,
          greaterThanIncDate: true,
          lessThanIncDate: true,
          truncMin: null,
          truncMax: null,
        });
      } else {
        this.setState({
          before: null,
          after: null,
          beforeVar: specificFormDate ? element.before_var : null,
          afterVar: specificFormDate ? element.after_var : null,
          greaterThanIncDate: null,
          lessThanIncDate: null,
          truncMin: specificFormDate ? element.trunc_min : null,
          truncMax: specificFormDate ? element.trunc_max : null,
        });
      }
    }
  }

  onSettingsChange = (e) => {
    const { element } = this.props;
    const newSettings = e.target.value;
    const specificDate = element.before && element.after && element.before === element.after;
    const incDate = element.less_than_inc_date && element.greater_than_inc_date;
    const specificFormDate = element.before_var && element.after_var
      && element.before_var === element.after_var;

    if (newSettings === SPECIFIC_DATE) {
      this.setState({
        typeOfSettings: e.target.value,
        before: specificDate ? element.before : null,
        after: specificDate ? element.after : null,
        beforeVar: specificFormDate ? element.before_var : null,
        afterVar: specificFormDate ? element.after_var : null,
        lessThanIncDate: incDate,
        greaterThanIncDate: incDate,
        truncMin: specificFormDate ? element.trunc_min : null,
        truncMax: specificFormDate ? element.trunc_max : null,
      });
    } else if (newSettings === INTERVAL) {
      this.setState({
        typeOfSettings: e.target.value,
        before: !specificDate ? element.before : null,
        after: !specificDate ? element.after : null,
        beforeVar: !specificFormDate ? element.before_var : null,
        afterVar: !specificFormDate ? element.after_var : null,
        lessThanIncDate: !incDate ? element.less_than_inc_date : null,
        greaterThanIncDate: !incDate ? element.greater_than_inc_date : null,
        truncMin: !specificFormDate ? element.trunc_min : null,
        truncMax: !specificFormDate ? element.trunc_max : null,
      });
    }
  }

  onCastChange = (date, newValue) => {
    if (date === 'after' || date === SPECIFIC_DATE) {
      this.setState({ truncMin: newValue ? DATE_FULL : null });
    }
    if (date === 'before' || date === SPECIFIC_DATE) {
      this.setState({ truncMax: newValue ? DATE_FULL : null });
    }
  }

  renderDateCheck = () => {
    const { t, element } = this.props;
    const {
      typeOfSettings, before, after, greaterThanIncDate, lessThanIncDate, beforeVar,
      afterVar, truncMin, truncMax, isConstraining,
    } = this.state;
    const { format } = element;
    const withTime = format === DATE_TIME;

    const dateTypeChoices = [
      {
        text: t(`project:consistency-check.interval${withTime ? '' : '-date'}`),
        value: INTERVAL,
      },
      {
        text: t('project:consistency-check.a-specific-date'),
        value: SPECIFIC_DATE,
      },
    ];

    return (
      <div>
        <div className="pb-2">
          <LabeledChoice
            name="date-type-of-control"
            label={t('project:consistency-check.response-must-be')}
            value={typeOfSettings}
            onChange={this.onSettingsChange}
            choices={dateTypeChoices}
            hideOptionalLabel
            colLabelClassName="col-12 pb-1"
          />
        </div>
        {
          (typeOfSettings === SPECIFIC_DATE) && (
            <div className="pt-3 form-group">
              <div className="mb-2">
                {t(`project:consistency-check.specific-${withTime ? 'value' : 'date'}`)}
              </div>
              <DateInput
                {...this.props}
                customDate={after}
                inclusionDate={greaterThanIncDate}
                formDate={afterVar}
                onChange={(dateType, value, truncTo) => this.onDateChange(
                  SPECIFIC_DATE, dateType, value, truncTo,
                )}
                onDateTypeChange={(dateType) => this.onDateTypeChange(
                  SPECIFIC_DATE, dateType,
                )}
                format={format}
                castToDate={truncMin === DATE_FULL}
                onCastChange={(value) => this.onCastChange(SPECIFIC_DATE, value)}
              />
            </div>
          )
        }
        {
          (typeOfSettings === INTERVAL) && (
            <div className="pt-3 form-group">
              <div className="mb-2 row align-items-center">
                <div className="col-auto pr-1">
                  {t(`project:consistency-check.limit-${withTime ? 'values' : 'dates'}`)}
                </div>
                <div className="col-auto px-0">
                  <small className="font-italic ml-1">
                    {t('project:consistency-check.at-least-one-limit')}
                  </small>
                </div>
              </div>
              <div className="py-2">
                <div className="font-italic small">
                  {t('project:consistency-check.minimum')}
                </div>
                <DateInput
                  {...this.props}
                  customDate={after}
                  inclusionDate={greaterThanIncDate}
                  formDate={afterVar}
                  onChange={(dateType, value, truncTo) => this.onDateChange(
                    'after', dateType, value, truncTo,
                  )}
                  onDateTypeChange={(dateType) => this.onDateTypeChange(
                    'after', dateType,
                  )}
                  format={format}
                  castToDate={truncMin === DATE_FULL}
                  onCastChange={(value) => this.onCastChange('after', value)}
                  checkboxId="cast-min-to-date"
                />
              </div>
              <div className="pt-3">
                <div className="font-italic small">
                  {t('project:consistency-check.maximum')}
                </div>
                <DateInput
                  {...this.props}
                  customDate={before}
                  inclusionDate={lessThanIncDate}
                  formDate={beforeVar}
                  onChange={(dateType, value, truncTo) => this.onDateChange(
                    'before', dateType, value, truncTo,
                  )}
                  onDateTypeChange={(dateType) => this.onDateTypeChange(
                    'before', dateType,
                  )}
                  format={format}
                  castToDate={truncMax === DATE_FULL}
                  onCastChange={(value) => this.onCastChange('before', value)}
                  checkboxId="cast-max-to-date"
                />
              </div>
            </div>
          )
        }

        {/* Constraining checkbox */}
        <hr className="my-3" />
        <div className="row">
          <div className="col-12">
            <div className="d-flex align-items-center">
              <Checkbox
                checked={isConstraining}
                onChange={(e) => this.setState({ isConstraining: e.target.checked })}
                color="danger"
              >
                {t('project:consistency-check.is-constraining')}
              </Checkbox>
              <Help iconClassName="ml-2">
                {t('project:consistency-check.is-constraining-tooltip')}
              </Help>
            </div>
            <div className="mt-1">
              <small className="font-italic text-orange">
                {t('project:consistency-check.is-constraining-description')}
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

    renderModalContent = () => {
      const { element } = this.props;
      switch (element.type) {
        case ELEMENT_TYPE_MEASUREMENT:
          return this.renderQuantitativeCheck();
        case ELEMENT_TYPE_CALCULATION:
          return this.renderCalculationCheck();
        case ELEMENT_TYPE_DATE:
          return this.renderDateCheck();
        case ELEMENT_TYPE_TEXT:
          return this.renderTextCheck();
        case ELEMENT_TYPE_UNIQUE_CHOICE:
        case ELEMENT_TYPE_MULTIPLE_CHOICES:
          if (element.implementable) {
            return this.renderListCheck();
          }
          return '';
        default:
          return '';
      }
    };

    canValidate = (type) => {
      if (type === ELEMENT_TYPE_CALCULATION || type === ELEMENT_TYPE_MEASUREMENT) {
        const { lowBound, highBound, forbidDecimals } = this.state;
        return (lowBound || highBound || forbidDecimals);
      }
      if (type === ELEMENT_TYPE_DATE) {
        const {
          before, after, beforeVar, afterVar, lessThanIncDate, greaterThanIncDate,
        } = this.state;
        return (before || after || beforeVar || afterVar || lessThanIncDate || greaterThanIncDate);
      }
      if (type === ELEMENT_TYPE_TEXT) {
        const {
          textConstraintType,
          textMinLength,
          textMaxLength,
          textFixedLength,
          textFormatEnabled,
          textFormatPattern,
        } = this.state;

        // Pas de contrainte de longueur = toujours valide (seulement filtres de caractères)
        if (textConstraintType === TEXT_CONSTRAINT_NONE) {
          return true;
        }
        if (textConstraintType === TEXT_CONSTRAINT_MIN_MAX) {
          return (textMinLength || textMaxLength);
        }
        if (!textFixedLength) return false;
        if (textFormatEnabled) {
          return textFormatPattern.length > 0 && this.validateTextPattern(textFormatPattern);
        }
        return true;
      }
      if (type === ELEMENT_TYPE_UNIQUE_CHOICE || type === ELEMENT_TYPE_MULTIPLE_CHOICES) {
        const {
          listConstraintType,
          listItemMinLength,
          listItemMaxLength,
          listItemFixedLength,
          listItemFormatEnabled,
          listItemFormatPattern,
        } = this.state;

        // Pas de contrainte de longueur = toujours valide (seulement filtres de caractères)
        if (listConstraintType === TEXT_CONSTRAINT_NONE) {
          return true;
        }
        if (listConstraintType === TEXT_CONSTRAINT_MIN_MAX) {
          return (listItemMinLength || listItemMaxLength);
        }

        if (!listItemFixedLength) return false;
        if (listItemFormatEnabled) {
          // eslint-disable-next-line max-len
          return listItemFormatPattern.length > 0 && this.validateTextPattern(listItemFormatPattern);
        }
        return true;
      }
      return false;
    };

    hasSettingsSaved = () => {
      const { element } = this.props;
      if (element.type === ELEMENT_TYPE_CALCULATION || element.type === ELEMENT_TYPE_MEASUREMENT) {
        return (this.getMin() || this.getMax() || element.integer);
      }
      if (element.type === ELEMENT_TYPE_DATE) {
        return (element.before || element.after || element.before_var || element.after_var
                || element.less_than_inc_date || element.greater_than_inc_date);
      }
      if (element.type === ELEMENT_TYPE_TEXT) {
        return (
          (element.text_min_length !== undefined && element.text_min_length !== null)
                || (element.text_max_length !== undefined && element.text_max_length !== null)
                || (element.text_fixed_length !== undefined && element.text_fixed_length !== null)
        );
      }
      if ((element.type === ELEMENT_TYPE_UNIQUE_CHOICE
                || element.type === ELEMENT_TYPE_MULTIPLE_CHOICES)
            && element.implementable) {
        return (element.list_item_min_length !== undefined && element.list_item_min_length !== null)
                // eslint-disable-next-line max-len
                || (element.list_item_max_length !== undefined && element.list_item_max_length !== null)
                // eslint-disable-next-line max-len
                || (element.list_item_fixed_length !== undefined && element.list_item_fixed_length !== null);
      }
      return false;
    };

    render() {
      const {
        t, children, elementName, element,
      } = this.props;
      const { loading } = this.state;

      const elementHasSettingsSaved = this.hasSettingsSaved();

      const getModalSize = () => {
        if (element.type === ELEMENT_TYPE_DATE) {
          return 'md';
        }
        if (element.type === ELEMENT_TYPE_TEXT
                  || element.type === ELEMENT_TYPE_UNIQUE_CHOICE
                  || element.type === ELEMENT_TYPE_MULTIPLE_CHOICES) {
          return 'lg';
        }
        return 'sm';
      };

      return (
        <NewModal
          trigger={children}
          title={t('project:consistency-check.modal-title')}
          xlHeader={elementName}
          size={getModalSize()}
          type={2}
          xlHeaderPlacement="top"
          onLoad={this.resyncSettingsParameters}
          footer={(
            <div className="flex-fill d-flex flex-column">
              <div className="row align-self-center">
                <button
                  type="button"
                  className="btn btn-newblue-1 text-white px-3"
                  onClick={() => {
                    if (element.type === ELEMENT_TYPE_DATE) {
                      this.updateDateConsistency();
                    } else if (element.type === ELEMENT_TYPE_TEXT) {
                      this.updateTextConsistency();
                    } else if ((element.type === ELEMENT_TYPE_UNIQUE_CHOICE
                        || element.type === ELEMENT_TYPE_MULTIPLE_CHOICES)
                        && element.implementable) {
                      this.updateListConsistency();
                    } else {
                      this.updateConsistency();
                    }
                  }}
                  disabled={!this.canValidate(element.type) || loading}
                >
                  {t('common:button.save')}
                </button>
                {
                elementHasSettingsSaved ? (
                  <ButtonConfirm
                    onClick={() => {
                      if (element.type === ELEMENT_TYPE_TEXT) {
                        this.deleteTextSettings();
                      // eslint-disable-next-line max-len
                      } else if ((element.type === ELEMENT_TYPE_UNIQUE_CHOICE || element.type === ELEMENT_TYPE_MULTIPLE_CHOICES) && element.implementable) {
                        this.deleteListSettings();
                      } else {
                        this.deleteSettings();
                      }
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-link ml-2"
                      disabled={loading}
                    >
                      {t('project:consistency-check.delete-settings')}
                    </button>
                  </ButtonConfirm>
                ) : (
                  <NewTooltip
                    content={t('common:tooltip.cancel')}
                  >
                    <button
                      type="button"
                      className="btn btn-link ml-2"
                      disabled={loading}
                      onClick={() => this.modal.hide()}
                    >
                      {t('common:button.cancel')}
                    </button>
                  </NewTooltip>
                )
              }
              </div>
              <div
                className="pt-5"
                style={{ padding: '0 4rem' }}
              >
                <FAQLink
                  articleId={11}
                />
              </div>
            </div>
        )}
          ref={(modal) => {
            this.modal = modal;
          }}
        >
          <div className="consistency-check-modal">
            {loading && <CardLoader />}
            {this.renderModalContent()}
          </div>
        </NewModal>
      );
    }
}

export default ConsistencyCheckManager;
